import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './theme.css';

// Components
import Navigation from './components/Navigation';
import Login from './components/auth/Login';
import AuthCallback from './components/auth/AuthCallback';
import Dashboard from './components/dashboard/Dashboard';
import ExpenseList from './components/expenses/ExpenseList';
import ExpenseDetail from './components/expenses/ExpenseDetail';
import ExpenseForm from './components/expenses/ExpenseForm';
import CategoryList from './components/categories/CategoryList';
import LabelList from './components/labels/LabelList';
import Profile from './components/profile/Profile';
import SalaryYearGrid from './components/salary/SalaryYearGrid';
import Statistics from './components/statistics/Statistics';
import AnalysisHub from './components/analysis/AnalysisHub';
import CategoryAnalysis from './components/analysis/CategoryAnalysis';
import NotFound from './components/common/NotFound';
import AddToHomeScreen from './components/common/AddToHomeScreen';
import PWAUpdateNotification, {
  notifyServiceWorkerUpdate,
} from './components/common/PWAUpdateNotification';

// Context
import { useAuth } from './context/AuthContext';

// Service Worker
import * as serviceWorkerRegistration from './utils/serviceWorkerRegistration';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="text-center p-5">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppContent() {
  return (
    <div className="app-shell">
      <AddToHomeScreen />
      <PWAUpdateNotification />
      <Navigation />
      <Container className="app-main">
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/expenses" element={
            <ProtectedRoute>
              <ExpenseList />
            </ProtectedRoute>
          } />
          <Route path="/expenses/new" element={
            <ProtectedRoute>
              <ExpenseForm />
            </ProtectedRoute>
          } />
          <Route path="/expenses/:id" element={
            <ProtectedRoute>
              <ExpenseDetail />
            </ProtectedRoute>
          } />
          <Route path="/expenses/:id/edit" element={
            <ProtectedRoute>
              <ExpenseForm />
            </ProtectedRoute>
          } />
          <Route path="/categories" element={
            <ProtectedRoute>
              <CategoryList />
            </ProtectedRoute>
          } />
          <Route path="/labels" element={
            <ProtectedRoute>
              <LabelList />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/salary" element={
            <ProtectedRoute>
              <SalaryYearGrid />
            </ProtectedRoute>
          } />
          <Route path="/statistics" element={
            <ProtectedRoute>
              <Statistics />
            </ProtectedRoute>
          } />
          <Route path="/analysis" element={
            <ProtectedRoute>
              <AnalysisHub />
            </ProtectedRoute>
          } />
          <Route path="/analysis/categories/:id" element={
            <ProtectedRoute>
              <CategoryAnalysis />
            </ProtectedRoute>
          } />
          
          {/* Not found route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Container>
    </div>
  );
}

function App() {
  return <AppContent />;
}

// Register service worker — prompt for refresh when a new version is waiting
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    notifyServiceWorkerUpdate(registration);
  },
});

export default App;
